import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Installer() {
  const navigate = useNavigate();

  // Force and persist light theme on installer page
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    try { localStorage.setItem('theme', 'light'); } catch { }
  }, []);

  const [form, setForm] = useState({
    licenseKey: '',
    licenseDomain: 'localhost',
    dbHost: 'localhost',
    dbPort: '3306',
    dbName: 'email_verifier',
    dbUser: 'root',
    dbPassword: '',
    adminUsername: 'admin',
    adminEmail: 'admin@example.com',
    adminPassword: '',
    confirmPassword: ''
  });

  const [step, setStep] = useState(1);
  const [showWelcome, setShowWelcome] = useState(true);
  const [licenseValidated, setLicenseValidated] = useState(false);
  const [dbTested, setDbTested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dbSuccess, setDbSuccess] = useState(false);
  const [dbWarning, setDbWarning] = useState('');
  const [showWipePopup, setShowWipePopup] = useState(false);
  const [restartInProgress, setRestartInProgress] = useState(false);
  const [restartDone, setRestartDone] = useState(false);
  const [showDbPassword, setShowDbPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const getUserFriendlyError = (error) => {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    if (error?.message) {
      return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
  };

  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await fetch('/api/setup/status', {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.isConfigured) {
          if (data.databaseReady) {
            window.location.href = '/login';
          } else {
            setStep('database');
            setForm(prev => ({ ...prev, ...data.config }));
          }
          return;
        }
      } catch (error) {
        // no-op
      }

      try {
        const pendingRes = await fetch('/api/setup/pending-license', { credentials: 'include' });
        const pending = await pendingRes.json();

        if (pending.found && pending.license_token) {
          localStorage.setItem('license_token', pending.license_token);
          setLicenseValidated(true);
          setForm(prev => ({
            ...prev,
            licenseKey: pending.licenseKey || prev.licenseKey,
            licenseDomain: pending.licenseDomain || prev.licenseDomain
          }));
          setStep(2);
          setProgress('✅ License already validated — continuing from where you left off.');
          setTimeout(() => setProgress(''), 3000);
        }
      } catch (_) {
        // non-critical
      }
    };

    checkSetupStatus();
  }, []);

  useEffect(() => {
    setForm(f => ({ ...f, licenseDomain: window.location.hostname }));
  }, []);


  useEffect(() => {
    if (setupComplete) {
      const redirectTimer = setTimeout(() => {
        window.location.href = '/login';
      }, 5000);

      return () => clearTimeout(redirectTimer);
    }
  }, [setupComplete]);



  const handleLicenseValidation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setProgress('Verifying purchase code and generating token...');

    try {
      const response = await fetch('/api/setup/validate-license', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseKey: form.licenseKey,
          licenseDomain: window.location.hostname
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setProgress('License validation successful!');
        if (data.license_token) {
          localStorage.setItem('license_token', data.license_token);
        }
        setLicenseValidated(true);
        setStep(2);
        setTimeout(() => {
          setProgress('');
          setLoading(false);
        }, 1500);
      } else {
        const userFriendlyError = getUserFriendlyError(data.error);
        setError(userFriendlyError);
        setProgress('');
        setLoading(false);
      }
    } catch (err) {
      const userFriendlyError = getUserFriendlyError(err.message);
      setError(userFriendlyError);
      setProgress('');
      setLoading(false);
    }
  };


  const handleDbTest = async (e) => {
    e.preventDefault();

    if (!licenseValidated) {
      setError('Please validate your license first');
      setStep(1);
      return;
    }

    setLoading(true);
    setError('');
    setDbWarning('');
    setProgress('Testing database connection...');
    setDbSuccess(false);
    try {
      const dbRes = await fetch('/api/setup/test-db-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dbHost: form.dbHost,
          dbPort: form.dbPort,
          dbName: form.dbName,
          dbUser: form.dbUser,
          dbPassword: form.dbPassword,
        }),
      });
      const dbData = await dbRes.json();
      if (!dbData.success) {
        setError(dbData.error || 'Could not connect to database.');
        setProgress('');
        setLoading(false);
        return;
      }

      setProgress('Database connection successful!');
      setDbSuccess(true);
      setDbTested(true);

      if (dbData.hasTables) {
        setShowWipePopup(true);
        setLoading(false);
      } else {
        setTimeout(() => {
          setStep(3);
          setProgress('');
          setLoading(false);
        }, 1500);
      }
    } catch (err) {
      setError(err.message);
      setProgress('');
      setLoading(false);
    }
  };


  const handleSetup = async (e) => {
    e.preventDefault();

    if (!licenseValidated || !dbTested) {
      setError('Please complete all previous steps first');
      return;
    }

    setLoading(true);
    setError('');
    setProgress('Setting up application...');
    try {
      const licenseToken = localStorage.getItem('license_token');
      const setupData = {
        ...form,
        license_token: licenseToken
      };

      const requestBody = JSON.stringify(setupData);
      const res = await fetch('/api/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: requestBody,
      });
      let data = {};
      try {
        data = await res.json();
      } catch (jsonErr) {
        throw new Error('Server did not return a valid response. Please try again.');
      }
      if (!res.ok) {
        if (data.errorType === 'database_error') {
          throw new Error(data.details || data.error || 'Database configuration error');
        } else if (data.errorType === 'system_error' && data.shouldReload) {
          setError(data.error || 'Setup failed due to a system error. The installer has been reset. Please try again.');
          setProgress('');
          setLoading(false);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
          return;
        } else if (data.error && data.error.includes('Purchase code validation failed')) {
          const errorMessage = data.error.replace('Purchase code validation failed: ', '');
          throw new Error(`Purchase code validation failed: ${errorMessage}`);
        } else {
          throw new Error(data.error || 'Setup failed');
        }
      }

      setProgress('Setup in progress... Please wait while we configure your application.');

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = 5000;

      const pollForCompletion = async () => {
        attempts++;
        setProgress(`Setup in progress... (${attempts}/${maxAttempts}) Please wait while we configure your application.`);

        try {
          const statusRes = await fetch('/api/setup/status', {
            credentials: 'include'
          });
          const statusData = await statusRes.json();

          if (statusData.isConfigured) {
            setProgress('Setup completed successfully! Server is restarting...');
            setSuccess(true);
            setSetupComplete(true);
            setLoading(false);
            setTimeout(() => {
              window.location.href = '/login';
            }, 5000);
            return;
          }

          if (attempts >= maxAttempts) {
            setError('Setup is taking longer than expected. Please check the server logs and try again.');
            setProgress('');
            setLoading(false);
            return;
          }

          setTimeout(pollForCompletion, pollInterval);
        } catch (pollError) {
          if (attempts >= 3 && (pollError.message.includes('Failed to fetch') || pollError.message.includes('Network Error'))) {
            setProgress('Setup completed! Server is restarting... Please wait.');
            setTimeout(() => {
              setProgress('Setup completed successfully! Redirecting to login...');
              setTimeout(() => {
                window.location.href = '/login';
              }, 3000);
            }, 10000);
            return;
          }

          if (attempts >= maxAttempts) {
            setError('Setup is taking longer than expected. Please check the server logs and try again.');
            setProgress('');
            setLoading(false);
          } else {
            setTimeout(pollForCompletion, pollInterval);
          }
        }
      };

      setTimeout(pollForCompletion, pollInterval);

    } catch (err) {
      if (err.message.includes('Failed to fetch') || err.message.includes('Network Error')) {
        setProgress('Setup completed! Server is restarting... Please wait.');
        setTimeout(() => {
          setProgress('Setup completed successfully! Redirecting to login...');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }, 10000);
      } else {
        setError(err.message);
        setProgress('');
        setLoading(false);
      }
    }
  };


  const handleWipeAndContinue = async () => {
    setShowWipePopup(false);
    setLoading(true);
    setError('');
    setProgress('Wiping database...');
    try {
      const res = await fetch('/api/setup/wipe-database', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dbHost: form.dbHost,
          dbPort: form.dbPort,
          dbName: form.dbName,
          dbUser: form.dbUser,
          dbPassword: form.dbPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Database wipe failed');
      setProgress('Database wiped successfully! Proceeding to account setup...');
      setTimeout(() => {
        setStep(3);
        setProgress('');
        setLoading(false);
      }, 2000);
    } catch (err) {
      setError(err.message);
      setProgress('');
      setLoading(false);
    }
  };


  const handleContinueWithExisting = () => {
    setShowWipePopup(false);
    setLoading(false);
    setStep(3);
  };

  const handleRestartServer = async () => {
    setLoading(true);
    setError('');
    setProgress('Restarting server...');

    try {
      const res = await fetch('/api/setup/force-restart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await res.json();

      if (data.success) {
        setProgress('Server restarting... Please wait...');
        let attempts = 0;
        const maxAttempts = 30;
        const pollInterval = 1000;

        const pollForRestart = async () => {
          attempts++;
          try {
            const statusRes = await fetch('/api/setup/status', {
              credentials: 'include'
            });

            if (statusRes.ok) {
              setProgress('Server restarted successfully!');
              setTimeout(() => {
                window.location.reload();
              }, 2000);
              return;
            }
          } catch (err) { }

          if (attempts >= maxAttempts) {
            setError('Server restart is taking longer than expected. Please refresh the page manually.');
            setProgress('');
            setLoading(false);
            return;
          }
          setTimeout(pollForRestart, pollInterval);
        };

        setTimeout(pollForRestart, pollInterval);
      } else {
        setError('Failed to restart server');
        setProgress('');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      setProgress('');
      setLoading(false);
    }
  };

  const handleRestartWorkers = async () => {
    setLoading(true);
    setError('');
    setProgress('Restarting workers...');

    try {
      const res = await fetch('/api/setup/restart-workers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await res.json();

      if (data.success) {
        setProgress('Workers restarted successfully!');
        setError('');
        setTimeout(() => {
          setProgress('');
          setLoading(false);
        }, 3000);
      } else {
        setError(data.error || 'Failed to restart workers');
        setProgress('');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      setProgress('');
      setLoading(false);
    }
  };



  if (showWelcome) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
        <div className="bg-white rounded-xl shadow-xl p-10 max-w-2xl w-full mx-4 border border-slate-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-2xl mb-6 border border-slate-200">
              <svg className="w-10 h-10 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Welcome to Setup
            </h1>

            <p className="text-xl text-slate-600 font-medium mb-3">
              Smart Email Verifier
            </p>

            <p className="text-slate-500 max-w-lg mx-auto leading-relaxed">
              Thank you for choosing our software. This wizard will help you configure your installation in just a few minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 text-center">
              <p className="font-semibold text-slate-800 text-sm">Performance</p>
              <p className="text-xs text-slate-500 mt-1">High-speed verification engine</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 text-center">
              <p className="font-semibold text-slate-800 text-sm">Accuracy</p>
              <p className="text-xs text-slate-500 mt-1">Advanced SMTP & DNS checks</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 text-center">
              <p className="font-semibold text-slate-800 text-sm">Security</p>
              <p className="text-xs text-slate-500 mt-1">Enterprise-grade data protection</p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-6 mb-10 border border-slate-200">
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6 text-center">
              System Support
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="flex items-start gap-3">
                <div className="text-slate-400 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-bold uppercase">Provider</p>
                  <p className="text-base font-semibold text-slate-800">UnCutSolutions</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="text-slate-400 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-bold uppercase">Technical Support</p>
                  <p className="text-sm font-semibold text-slate-800">support@smartemailverifier.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowWelcome(false)}
              className="w-full py-4 px-8 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors shadow-md flex items-center justify-center gap-3"
            >
              Begin Installation
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <p className="mt-4 text-xs text-slate-400 uppercase tracking-widest font-bold">
              Ready to deploy in minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl shadow-xl p-10 max-w-md w-full border border-slate-200 text-center">
          <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Configuration Complete</h2>
          <p className="text-slate-600 mb-8">Your application has been successfully configured and is ready for use.</p>

          {restartDone ? (
            <button
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors"
            >
              Refresh Application
            </button>
          ) : (
            <div className="flex items-center justify-center gap-3 text-slate-500">
              <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-slate-800 rounded-full"></div>
              <span className="font-medium text-sm uppercase tracking-wider">Restarting Server...</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
      <div className="bg-white rounded-xl shadow-xl p-10 w-full max-w-xl border border-slate-200">
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center text-white">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Deployment Wizard</h1>
              <p className="text-slate-500 text-sm">Follow the steps below to finish setup</p>
            </div>
          </div>

          {/* Step Indicators */}
          <div className="grid grid-cols-3 gap-2">
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${step >= 1 ? 'bg-slate-900' : 'bg-slate-200'}`}></div>
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${step >= 2 ? 'bg-slate-900' : 'bg-slate-200'}`}></div>
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${step >= 3 ? 'bg-slate-900' : 'bg-slate-200'}`}></div>
          </div>
          <div className="flex justify-between mt-2 px-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${step >= 1 ? 'text-slate-900' : 'text-slate-400'}`}>License</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${step >= 2 ? 'text-slate-900' : 'text-slate-400'}`}>Database</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${step >= 3 ? 'text-slate-900' : 'text-slate-400'}`}>Account</span>
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={handleLicenseValidation} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Purchase Code</label>
                <input
                  name="licenseKey"
                  value={form.licenseKey}
                  onChange={handleChange}
                  required
                  placeholder="e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="input-professional"
                />
                <p className="text-xs text-slate-400 mt-2">Your unique Envato purchase code or license key.</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Target Domain</label>
                <input
                  name="licenseDomain"
                  value={form.licenseDomain}
                  readOnly
                  className="input-professional bg-slate-50 text-slate-500 cursor-not-allowed border-dashed"
                />
              </div>
            </div>

            {progress && (
              <div className="bg-slate-900 rounded-lg p-4 flex items-center gap-3">
                <div className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-white rounded-full"></div>
                <p className="text-xs font-bold text-white uppercase tracking-wider">{progress}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-bold text-red-900 uppercase tracking-tight">Validation Error</h3>
                    <p className="mt-1 text-sm text-red-700 font-medium">{getUserFriendlyError(error)}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-slate-900 text-white rounded-lg font-bold shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-slate-500 border-t-white rounded-full"></div>
                  Verifying...
                </>
              ) : (
                <>
                  Verify License
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </>
              )}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleDbTest} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Host</label>
                <input name="dbHost" value={form.dbHost} onChange={handleChange} required placeholder="localhost" className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Port</label>
                <input name="dbPort" value={form.dbPort} onChange={handleChange} required placeholder="3306" className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Database Name</label>
                <input name="dbName" value={form.dbName} onChange={handleChange} required placeholder="email_verifier" className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Username</label>
                <input name="dbUser" value={form.dbUser} onChange={handleChange} required placeholder="root" className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Password</label>
                <input
                  name="dbPassword"
                  value={form.dbPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  type={showDbPassword ? 'text' : 'password'}
                  className="input-professional"
                  onMouseEnter={() => setShowDbPassword(true)}
                  onMouseLeave={() => setShowDbPassword(false)}
                />
              </div>
            </div>

            {progress && (
              <div className="bg-slate-900 rounded-lg p-4 flex items-center gap-3">
                <div className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-white rounded-full"></div>
                <p className="text-xs font-bold text-white uppercase tracking-wider">{progress}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-bold text-red-900 uppercase tracking-tight">Database Error</h3>
                    <p className="mt-1 text-sm text-red-700 font-medium">{getUserFriendlyError(error)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-slate-900 text-white rounded-lg font-bold shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-slate-500 border-t-white rounded-full"></div>
                    Testing...
                  </>
                ) : (
                  <>
                    Test Connection
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                  </>
                )}
              </button>

              {dbSuccess && !loading && (
                <button
                  type="button"
                  className="w-full py-4 bg-white border-2 border-slate-900 text-slate-900 rounded-lg font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                  onClick={() => setStep(3)}
                >
                  Continue to Next Step
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleSetup} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Admin Username</label>
                <input name="adminUsername" value={form.adminUsername} onChange={handleChange} required className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Admin Email</label>
                <input name="adminEmail" value={form.adminEmail} onChange={handleChange} required type="email" className="input-professional" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-widest">Admin Password</label>
                <input
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  required
                  type={showAdminPassword ? 'text' : 'password'}
                  className="input-professional"
                  onMouseEnter={() => setShowAdminPassword(true)}
                  onMouseLeave={() => setShowAdminPassword(false)}
                />
              </div>
            </div>

            {progress && (
              <div className="bg-slate-900 rounded-lg p-4 flex items-center gap-3">
                <div className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-white rounded-full"></div>
                <p className="text-xs font-bold text-white uppercase tracking-wider">{progress}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-bold text-red-900 uppercase tracking-tight">Finalization Error</h3>
                    <p className="mt-1 text-sm text-red-700 font-medium">{getUserFriendlyError(error)}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-slate-900 text-white rounded-lg font-bold shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-slate-500 border-t-white rounded-full"></div>
                  Configuring System...
                </>
              ) : (
                <>
                  Complete & Launch
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          </form>
        )}

        {setupComplete && (
          <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
            <div className="bg-white p-12 max-w-lg w-full text-center">
              <div className="w-20 h-20 bg-slate-900 text-white rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"></path>
                </svg>
              </div>

              <h3 className="text-3xl font-bold text-slate-900 mb-4">Setup Successful</h3>
              <p className="text-slate-500 mb-10 leading-relaxed">
                Smart Email Verifier has been successfully installed. You can now access your dashboard and start verifying emails.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 text-slate-500">
                  <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-slate-800 rounded-full"></div>
                  <span className="font-bold text-xs uppercase tracking-widest">Redirecting to Dashboard...</span>
                </div>
                <button
                  onClick={() => window.location.href = '/login'}
                  className="w-full py-4 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-all shadow-lg"
                >
                  Access Login Now
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .input-professional {
            width: 100%;
            padding: 0.875rem 1rem;
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            color: #1a202c;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
            outline: none;
          }
          .input-professional:focus {
            border-color: #1a202c;
            box-shadow: 0 0 0 1px #1a202c;
          }
          .input-professional::placeholder {
            color: #a0aec0;
            font-weight: 400;
          }
          .input-professional:hover {
            border-color: #cbd5e0;
          }
        `}</style>
      </div>

      {showWipePopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 border border-slate-200">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Existing Data Detected</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                We found existing tables in this database. Proceeding with a fresh install will permanently delete all existing records.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleWipeAndContinue}
                disabled={loading}
                className="w-full py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
              >
                {loading ? 'Wiping...' : 'Wipe & Continue Fresh'}
              </button>
              <button
                onClick={handleContinueWithExisting}
                disabled={loading}
                className="w-full py-3 bg-slate-100 text-slate-900 rounded-lg font-bold hover:bg-slate-200 transition-all"
              >
                Use Existing Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}